import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
    Buildings, Camera, FilePdf, Phone, Plus, Trash, Truck, X, Warning, Pencil,
} from "@phosphor-icons/react";
import LivePlateScanner from "./LivePlateScanner";

const CATEGORIES = [
    "Service Level Agreement",
    "Section 37.2 Agreement",
    "Letter of Good Standing",
    "Insurance",
    "Public Liability",
    "Other",
];

/**
 * Transporters — contractor haulage companies used for GTRB Tronox outbound export.
 * Admins/Managers register a transporter, their trucks (plates), and upload docs.
 * When a plate is scanned on the Load-Out Station the transporter is auto-attached.
 */
export default function Transporters() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [scorecard, setScorecard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // {type, transporter?}
    const canEdit = ["admin", "manager"].includes(user?.role);
    const canDelete = user?.role === "admin";

    const load = async () => {
        try {
            const [r, s] = await Promise.all([
                api.get("/fleet/transporters"),
                api.get("/fleet/transporters/scorecard"),
            ]);
            setItems(r.data || []);
            setScorecard((s.data || []).filter((x) => x.trips > 0));
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-4" data-testid="transporters-view">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Fleet · Transporters</div>
                    <div className="text-xl font-black text-[var(--grindrod-navy)]">Contractor haulage companies</div>
                </div>
                {canEdit && (
                    <button onClick={() => setModal({ type: "new" })} className="g-btn g-btn-primary" data-testid="add-transporter-btn">
                        <Plus size={16} weight="bold" /> Add transporter
                    </button>
                )}
            </div>
            {loading ? (
                <div className="text-center text-slate-500 py-10">Loading…</div>
            ) : (
                <>
                    {scorecard.length > 0 && (
                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="transporter-scorecard">
                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Fleet performance scorecard</div>
                                <div className="text-[10px] text-slate-500">Aggregated across all Tronox exports (active & completed) — ranked by trips.</div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                                        <tr>
                                            <th className="text-left px-3 py-1.5">Transporter</th>
                                            <th className="text-right px-2 py-1.5">Exports</th>
                                            <th className="text-right px-2 py-1.5">Trips</th>
                                            <th className="text-right px-2 py-1.5">Plates</th>
                                            <th className="text-right px-2 py-1.5">Skips</th>
                                            <th className="text-right px-2 py-1.5">Weighbridge&nbsp;tons</th>
                                            <th className="text-right px-2 py-1.5">Avg&nbsp;turnaround</th>
                                            <th className="text-right px-3 py-1.5">Avg&nbsp;on-station</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scorecard.map((s) => (
                                            <tr key={s.transporter_id} className="border-t border-slate-100" data-testid={`sc-row-${s.transporter_id}`}>
                                                <td className="px-3 py-1.5 font-bold text-[var(--grindrod-navy)]">{s.company_name}</td>
                                                <td className="px-2 py-1.5 text-right tabular">{s.exports_participated}</td>
                                                <td className="px-2 py-1.5 text-right tabular font-bold">{s.trips}</td>
                                                <td className="px-2 py-1.5 text-right tabular">{s.unique_plates}</td>
                                                <td className="px-2 py-1.5 text-right tabular">{s.skips}</td>
                                                <td className="px-2 py-1.5 text-right tabular">{Number(s.tons || 0).toLocaleString()} t</td>
                                                <td className="px-2 py-1.5 text-right tabular text-slate-500">{s.avg_turnaround_minutes != null ? `${s.avg_turnaround_minutes} min` : "—"}</td>
                                                <td className="px-3 py-1.5 text-right tabular text-slate-500">{s.avg_on_station_minutes != null ? `${s.avg_on_station_minutes} min` : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {items.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                            <Buildings size={32} className="mx-auto text-slate-400 mb-2" />
                            <div className="text-slate-700 font-bold">No transporters registered yet.</div>
                            {canEdit && <div className="text-slate-500 text-sm mt-1">Tap <b>Add transporter</b> to register the first contractor.</div>}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {items.map((t) => (
                                <TransporterCard
                                    key={t.id}
                                    t={t}
                                    canEdit={canEdit}
                                    canDelete={canDelete}
                                    onEdit={() => setModal({ type: "edit", transporter: t })}
                                    onDelete={async () => {
                                        if (!window.confirm(`Delete ${t.company_name}? All trucks and documents will be removed too.`)) return;
                                        try { await api.delete(`/fleet/transporters/${t.id}`); toast.success("Transporter removed."); load(); }
                                        catch (e) { toast.error(formatApiError(e)); }
                                    }}
                                    onManage={() => setModal({ type: "manage", transporter: t })}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {(modal?.type === "new" || modal?.type === "edit") && (
                <TransporterFormModal
                    initial={modal.transporter}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); load(); }}
                />
            )}
            {modal?.type === "manage" && (
                <TransporterManageModal
                    transporter={modal.transporter}
                    canEdit={canEdit}
                    onClose={() => { setModal(null); load(); }}
                    onSaved={load}
                />
            )}
        </div>
    );
}

function TransporterCard({ t, canEdit, canDelete, onEdit, onDelete, onManage }) {
    const today = new Date().toISOString().slice(0, 10);
    const expired = (t.documents || []).filter((d) => d.expiry_date && d.expiry_date < today);
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`transporter-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-base font-black text-[var(--grindrod-navy)] truncate" data-testid={`transporter-name-${t.id}`}>{t.company_name}</div>
                    {t.vat_number && <div className="text-[11px] text-slate-500">VAT · {t.vat_number}</div>}
                </div>
                <div className="flex gap-1">
                    {canEdit && <button onClick={onEdit} className="p-1.5 text-slate-500 hover:text-slate-900 rounded hover:bg-slate-100" title="Edit"><Pencil size={14} /></button>}
                    {canDelete && <button onClick={onDelete} className="p-1.5 text-rose-500 hover:text-rose-700 rounded hover:bg-rose-50" title="Delete" data-testid={`transporter-delete-${t.id}`}><Trash size={14} /></button>}
                </div>
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                {t.emergency_contact_name && <div><b>Emergency contact:</b> {t.emergency_contact_name}</div>}
                {t.emergency_contact_phone && <div className="flex items-center gap-1"><Phone size={11} /> {t.emergency_contact_phone}</div>}
                {t.email && <div className="truncate">✉ {t.email}</div>}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded bg-slate-50 border border-slate-200 py-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Trucks</div>
                    <div className="text-lg font-black text-[var(--grindrod-navy)]">{t.truck_count || 0}</div>
                </div>
                <div className="rounded bg-slate-50 border border-slate-200 py-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Docs</div>
                    <div className="text-lg font-black text-[var(--grindrod-navy)]">{(t.documents || []).length}</div>
                </div>
                <div className={`rounded py-1 border ${expired.length > 0 ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Expired</div>
                    <div className={`text-lg font-black ${expired.length > 0 ? "text-rose-600" : "text-slate-400"}`}>{expired.length}</div>
                </div>
            </div>
            <button onClick={onManage} className="w-full mt-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold" data-testid={`transporter-manage-${t.id}`}>
                Manage trucks & documents
            </button>
        </div>
    );
}

function TransporterFormModal({ initial, onClose, onSaved }) {
    const isEdit = !!initial?.id;
    const [form, setForm] = useState({
        company_name: initial?.company_name || "",
        vat_number: initial?.vat_number || "",
        emergency_contact_name: initial?.emergency_contact_name || "",
        emergency_contact_phone: initial?.emergency_contact_phone || "",
        alt_phone: initial?.alt_phone || "",
        email: initial?.email || "",
        address: initial?.address || "",
        notes: initial?.notes || "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.company_name.trim()) return toast.error("Company name is required.");
        setBusy(true);
        try {
            if (isEdit) await api.patch(`/fleet/transporters/${initial.id}`, form);
            else await api.post("/fleet/transporters", form);
            toast.success(isEdit ? "Transporter updated." : "Transporter added.");
            onSaved();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };
    const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="transporter-form-modal">
            <div className="bg-white text-slate-900 rounded-xl w-full max-w-lg shadow-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <div className="font-black">{isEdit ? "Edit transporter" : "Register transporter"}</div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1"><X size={20} /></button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-3 overflow-y-auto">
                    <label className="block">
                        <span className="text-[11px] uppercase text-slate-500 tracking-wider">Company name *</span>
                        <input className="g-input mt-1" data-testid="tp-input-company-name" {...field("company_name")} autoFocus />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[11px] uppercase text-slate-500 tracking-wider">VAT number</span>
                            <input className="g-input mt-1" {...field("vat_number")} />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase text-slate-500 tracking-wider">Email</span>
                            <input className="g-input mt-1" type="email" {...field("email")} />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[11px] uppercase text-slate-500 tracking-wider">Emergency contact name</span>
                        <input className="g-input mt-1" data-testid="tp-input-contact-name" {...field("emergency_contact_name")} />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[11px] uppercase text-slate-500 tracking-wider">Emergency phone</span>
                            <input className="g-input mt-1" data-testid="tp-input-contact-phone" {...field("emergency_contact_phone")} />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase text-slate-500 tracking-wider">Alt phone</span>
                            <input className="g-input mt-1" {...field("alt_phone")} />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[11px] uppercase text-slate-500 tracking-wider">Head office address</span>
                        <input className="g-input mt-1" {...field("address")} />
                    </label>
                    <label className="block">
                        <span className="text-[11px] uppercase text-slate-500 tracking-wider">Notes</span>
                        <textarea className="g-input mt-1" rows={2} {...field("notes")} />
                    </label>
                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 rounded bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold">Cancel</button>
                        <button type="submit" disabled={busy} className="flex-1 g-btn g-btn-primary" data-testid="tp-form-save">
                            {busy ? "Saving…" : isEdit ? "Save changes" : "Register"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function TransporterManageModal({ transporter, canEdit, onClose, onSaved }) {
    const [t, setT] = useState(transporter);
    const [tab, setTab] = useState("trucks"); // trucks | docs | incidents
    const [newPlate, setNewPlate] = useState("");
    const [newMake, setNewMake] = useState("");
    const [newModel, setNewModel] = useState("");
    const [newColor, setNewColor] = useState("");
    const [newLicDisk, setNewLicDisk] = useState("");
    const [newLicExpiry, setNewLicExpiry] = useState("");
    const [editingTruck, setEditingTruck] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanBusy, setScanBusy] = useState(false);

    const refresh = async () => {
        try { const r = await api.get(`/fleet/transporters/${transporter.id}`); setT(r.data); onSaved(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const resetTruckForm = () => {
        setNewPlate(""); setNewMake(""); setNewModel(""); setNewColor("");
        setNewLicDisk(""); setNewLicExpiry("");
    };

    const addPlate = async (e) => {
        e?.preventDefault?.();
        const plate = (newPlate || "").trim().toUpperCase();
        if (!plate) return;
        const payload = {
            plate,
            make: newMake || undefined,
            model: newModel || undefined,
            color: newColor || undefined,
            license_disk_number: newLicDisk || undefined,
            license_expiry_date: newLicExpiry || undefined,
        };
        try { await api.post(`/fleet/transporters/${transporter.id}/trucks`, payload); resetTruckForm(); toast.success(`${plate} added.`); refresh(); }
        catch (e2) { toast.error(formatApiError(e2)); }
    };
    const removePlate = async (plateNorm) => {
        if (!window.confirm("Remove this truck from the transporter?")) return;
        try { await api.delete(`/fleet/transporters/${transporter.id}/trucks/${encodeURIComponent(plateNorm)}`); toast.success("Truck removed."); refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const saveTruckEdit = async () => {
        if (!editingTruck) return;
        const payload = {
            plate: editingTruck.plate,
            make: editingTruck.make || undefined,
            model: editingTruck.model || undefined,
            color: editingTruck.color || undefined,
            license_disk_number: editingTruck.license_disk_number || undefined,
            license_expiry_date: editingTruck.license_expiry_date || undefined,
        };
        try {
            await api.patch(`/fleet/transporters/${transporter.id}/trucks/${encodeURIComponent(editingTruck.plate_normalised)}`, payload);
            toast.success("Truck updated.");
            setEditingTruck(null);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const onPlateCaptured = async (base64) => {
        setScanning(false); setScanBusy(true);
        try {
            const r = await api.post("/tronox/plate-scan", { image_base64: base64, mime_type: "image/jpeg" });
            if (r.data?.ok && r.data.plate) setNewPlate(r.data.plate);
            else toast.error(r.data?.reason || "Could not read plate.");
        } catch (e) { toast.error(formatApiError(e)); }
        setScanBusy(false);
    };
    const removeDoc = async (docId) => {
        if (!window.confirm("Remove this document?")) return;
        try { await api.delete(`/fleet/transporters/documents/${docId}`); toast.success("Document removed."); refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const downloadDoc = async (docId, filename) => {
        try {
            const r = await api.get(`/fleet/transporters/documents/${docId}/file`);
            const { file_base64, mime, filename: fn } = r.data;
            const bin = atob(file_base64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const blob = new Blob([bytes], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = fn || filename; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (scanning) return <LivePlateScanner onCapture={onPlateCaptured} onCancel={() => setScanning(false)} />;

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="transporter-manage-modal">
            <div className="bg-white text-slate-900 rounded-xl w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Manage transporter</div>
                        <div className="text-lg font-black">{t.company_name}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1"><X size={20} /></button>
                </div>
                <div className="border-b border-slate-200 flex">
                    {[
                        { k: "trucks",    label: `Trucks (${(t.trucks || []).length})` },
                        { k: "docs",      label: `Documents (${(t.documents || []).length})` },
                        { k: "incidents", label: `Incidents (${(t.incidents || []).length})` },
                    ].map((x) => (
                        <button key={x.k} onClick={() => setTab(x.k)} className={`px-3 py-2 text-xs font-bold uppercase tracking-wider ${tab === x.k ? "text-[var(--grindrod-navy)] border-b-2 border-[var(--grindrod-navy)]" : "text-slate-500 hover:text-slate-800"}`} data-testid={`tp-manage-tab-${x.k}`}>
                            {x.label}
                        </button>
                    ))}
                </div>
                <div className="p-4 overflow-y-auto flex-1">
                    {tab === "trucks" && (
                        <div>
                            {canEdit && (
                                <form onSubmit={addPlate} className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 space-y-2" data-testid="tp-truck-form">
                                    <div className="flex gap-2">
                                        <input value={newPlate} onChange={(e) => setNewPlate(e.target.value.toUpperCase())} placeholder="Plate — e.g. ND 234 GP" className="g-input flex-1 font-mono tracking-wider" data-testid="tp-add-plate-input" />
                                        <button type="button" onClick={() => setScanning(true)} disabled={scanBusy} className="inline-flex items-center gap-1 px-3 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-300 text-white text-sm font-bold" data-testid="tp-scan-plate">
                                            <Camera size={16} weight="bold" /> {scanBusy ? "Reading…" : "Scan"}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <input value={newMake} onChange={(e) => setNewMake(e.target.value)} placeholder="Make" className="g-input text-sm" data-testid="tp-truck-make" />
                                        <input value={newModel} onChange={(e) => setNewModel(e.target.value)} placeholder="Model" className="g-input text-sm" data-testid="tp-truck-model" />
                                        <input value={newColor} onChange={(e) => setNewColor(e.target.value)} placeholder="Colour" className="g-input text-sm" data-testid="tp-truck-color" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input value={newLicDisk} onChange={(e) => setNewLicDisk(e.target.value)} placeholder="Licence disk #" className="g-input text-sm" data-testid="tp-truck-lic-num" />
                                        <label className="block">
                                            <input type="date" value={newLicExpiry} onChange={(e) => setNewLicExpiry(e.target.value)} className="g-input text-sm w-full" data-testid="tp-truck-lic-expiry" />
                                            <span className="text-[10px] text-slate-500">Licence disk expiry</span>
                                        </label>
                                    </div>
                                    <button type="submit" className="w-full g-btn g-btn-primary" data-testid="tp-add-plate-submit">Add truck</button>
                                </form>
                            )}
                            {(t.trucks || []).length === 0 ? (
                                <div className="text-center py-6 text-slate-400 text-sm">No trucks registered. Add a plate above.</div>
                            ) : (
                                <div className="space-y-2">
                                    {t.trucks.map((tr) => {
                                        const isEditing = editingTruck?.id === tr.id;
                                        const today = new Date().toISOString().slice(0, 10);
                                        const licExpired = tr.license_expiry_date && tr.license_expiry_date < today;
                                        const licSoon = tr.license_expiry_date && !licExpired && ((new Date(tr.license_expiry_date) - new Date()) / 86400000) <= 30;
                                        return (
                                            <div key={tr.id} className="rounded border border-slate-200 bg-white" data-testid={`tp-truck-row-${tr.plate_normalised}`}>
                                                <div className="flex items-center justify-between px-3 py-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <Truck size={16} className="text-slate-500" />
                                                        <span className="font-mono font-bold">{tr.plate}</span>
                                                        {[tr.make, tr.model].filter(Boolean).length > 0 && (
                                                            <span className="text-[11px] text-slate-600 truncate">· {[tr.make, tr.model].filter(Boolean).join(" ")}{tr.color ? ` · ${tr.color}` : ""}</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {tr.license_expiry_date && (
                                                            <span
                                                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                                                                    licExpired ? "bg-rose-100 text-rose-700 border-rose-200"
                                                                    : licSoon ? "bg-amber-100 text-amber-700 border-amber-200"
                                                                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                                }`}
                                                                title={`Licence expiry ${tr.license_expiry_date}`}
                                                            >
                                                                {licExpired ? "LIC EXPIRED" : licSoon ? "LIC SOON" : "LIC OK"} · {tr.license_expiry_date}
                                                            </span>
                                                        )}
                                                        {canEdit && (
                                                            <>
                                                                <button onClick={() => setEditingTruck({ ...tr })} className="text-slate-500 hover:text-slate-900 text-xs" title="Edit metadata" data-testid={`tp-truck-edit-${tr.plate_normalised}`}><Pencil size={13} /></button>
                                                                <button onClick={() => removePlate(tr.plate_normalised)} className="text-rose-500 hover:text-rose-700 text-xs" data-testid={`tp-truck-remove-${tr.plate_normalised}`}><Trash size={14} /></button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                {isEditing && (
                                                    <div className="border-t border-slate-200 px-3 py-2 bg-slate-50 space-y-2" data-testid={`tp-truck-edit-form-${tr.plate_normalised}`}>
                                                        <div className="grid grid-cols-3 gap-2">
                                                            <input value={editingTruck.make || ""} onChange={(e) => setEditingTruck({ ...editingTruck, make: e.target.value })} placeholder="Make" className="g-input text-sm" />
                                                            <input value={editingTruck.model || ""} onChange={(e) => setEditingTruck({ ...editingTruck, model: e.target.value })} placeholder="Model" className="g-input text-sm" />
                                                            <input value={editingTruck.color || ""} onChange={(e) => setEditingTruck({ ...editingTruck, color: e.target.value })} placeholder="Colour" className="g-input text-sm" />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <input value={editingTruck.license_disk_number || ""} onChange={(e) => setEditingTruck({ ...editingTruck, license_disk_number: e.target.value })} placeholder="Licence disk #" className="g-input text-sm" />
                                                            <input type="date" value={editingTruck.license_expiry_date || ""} onChange={(e) => setEditingTruck({ ...editingTruck, license_expiry_date: e.target.value })} className="g-input text-sm" />
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button onClick={() => setEditingTruck(null)} className="flex-1 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-bold">Cancel</button>
                                                            <button onClick={saveTruckEdit} className="flex-1 g-btn g-btn-primary" data-testid={`tp-truck-edit-save-${tr.plate_normalised}`}>Save changes</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {tab === "docs" && (
                        <DocsPanel t={t} canEdit={canEdit} onDelete={removeDoc} onDownload={downloadDoc} onUploaded={refresh} />
                    )}
                    {tab === "incidents" && (
                        <IncidentsPanel t={t} />
                    )}
                </div>
            </div>
        </div>
    );
}

function DocsPanel({ t, canEdit, onDelete, onDownload, onUploaded }) {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [expiry, setExpiry] = useState("");
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const today = new Date().toISOString().slice(0, 10);

    const submit = async (e) => {
        e.preventDefault();
        const f = fileRef.current?.files?.[0];
        if (!f) return toast.error("Choose a file.");
        if (!title.trim()) return toast.error("Title is required.");
        if (f.size > 6 * 1024 * 1024) return toast.error("File too large (max 6 MB).");
        setBusy(true);
        try {
            const b64 = await new Promise((res, rej) => {
                const r = new FileReader();
                r.onload = () => res(String(r.result).split(",")[1] || "");
                r.onerror = rej;
                r.readAsDataURL(f);
            });
            await api.post(`/fleet/transporters/${t.id}/documents`, {
                title: title.trim(), category, expiry_date: expiry || undefined,
                filename: f.name, mime: f.type || "application/octet-stream", file_base64: b64,
            });
            toast.success("Document uploaded.");
            setTitle(""); setExpiry("");
            if (fileRef.current) fileRef.current.value = "";
            onUploaded();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };

    return (
        <div>
            {canEdit && (
                <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-3 space-y-2" data-testid="tp-doc-upload-form">
                    <div className="grid grid-cols-2 gap-2">
                        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Doc title" className="g-input" data-testid="tp-doc-title" />
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className="g-input" data-testid="tp-doc-category">
                            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="g-input" data-testid="tp-doc-expiry" />
                        <input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png" className="text-xs" data-testid="tp-doc-file" />
                    </div>
                    <button type="submit" disabled={busy} className="w-full g-btn g-btn-primary" data-testid="tp-doc-upload-submit">
                        {busy ? "Uploading…" : "Upload document"}
                    </button>
                </form>
            )}
            {(t.documents || []).length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm">No documents uploaded.</div>
            ) : (
                <div className="space-y-1">
                    {t.documents.map((d) => {
                        const expired = d.expiry_date && d.expiry_date < today;
                        return (
                            <div key={d.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded border ${expired ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`} data-testid={`tp-doc-row-${d.id}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <FilePdf size={18} className={expired ? "text-rose-500" : "text-slate-500"} />
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold truncate">{d.title}</div>
                                        <div className="text-[10px] text-slate-500">
                                            {d.category} · {d.filename}{d.expiry_date ? ` · expires ${d.expiry_date}` : ""}
                                            {expired && <span className="ml-1 text-rose-600 font-bold">EXPIRED</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => onDownload(d.id, d.filename)} className="text-slate-600 hover:text-slate-900 text-xs px-2 py-1 rounded bg-white border border-slate-200" data-testid={`tp-doc-download-${d.id}`}>Download</button>
                                    {canEdit && <button onClick={() => onDelete(d.id)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`tp-doc-remove-${d.id}`}><Trash size={14} /></button>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function IncidentsPanel({ t }) {
    const items = t.incidents || [];
    if (items.length === 0) return <div className="text-center py-6 text-slate-400 text-sm">No incidents logged against this transporter.</div>;
    return (
        <div className="space-y-1">
            {items.map((i) => (
                <div key={i.id} className="rounded border border-slate-200 bg-slate-50 p-3" data-testid={`tp-incident-${i.id}`}>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        <span>{new Date(i.occurred_at).toLocaleString()}</span>
                        <span className="inline-flex items-center gap-1 text-rose-600"><Warning size={11} weight="fill" /> {i.incident_type.replace(/_/g, " ")}</span>
                    </div>
                    <div className="text-sm text-slate-800 mt-1">{i.description}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Logged by {i.logged_by || "—"} · Hours lost: {i.hours_lost ?? 0}</div>
                </div>
            ))}
            <div className="text-[10px] text-slate-500 mt-2">To log a new incident against this transporter, use the Safety Incident form and pick this transporter.</div>
        </div>
    );
}
